<template>
  <div
    class="relative w-full media-player"
    :class="isHotelGallery ? 'height-inherit' : aspectRatio ? null : 'h-full'"
    :style="{ aspectRatio }"
  >
    <div class="size-full b-skeleton-background b-skeleton-animate">
      <div
        v-if="!play && !isDemioUrl"
        class="media-player-play-button size-full absolute"
        :class="playButtonClass"
        @click="onPlay"
      >
        <i-play-button
          class="media-player-play-button"
          filled
          :font-controlled="false"
          alt="Play movie"
          width="65"
        />
      </div>
      <template v-if="!play && initialized">
        <LazyResponsiveImage
          v-if="customThumbnail"
          :image="customThumbnail.url"
          :type="customThumbnail.type"
          :class="thumbnailClasses"
          :aspect-ratio="aspectRatio"
          gravity="center"
          :alt="alt"
        />
        <img
          v-else-if="type === 'youtube'"
          class="youtube_thumb size-full absolute media-player-thumb clip-inset"
          :src="`https://img.youtube.com/vi/${location}/${thumbnailFile}`"
          :class="thumbnailClasses"
          loading="lazy"
        />
      </template>
      <template v-if="play">
        <iframe
          v-if="initialized && !justPreview && type !== 'internal'"
          class="size-full z-[1]"
          frameborder="0"
          allowfullscreen
          mozallowfullscreen
          webkitallowfullscreen
          :allow="play ? 'autoplay' : ''"
          :src="src"
        />
        <video
          v-else-if="justPreview || type === 'internal'"
          class="size-full"
          :autoplay="autoplay"
          :poster="justPreviewPoster"
          playsinline="playsinline"
          loop
          :muted="justPreview"
          :controls="!justPreview"
          :controlslist="justPreview ? '' : 'nodownload'"
          preload="metadata"
        >
          <source
            :src="src"
            type="video/mp4"
          />
        </video>
      </template>
    </div>
  </div>
</template>

<script>
// TODO: Extend to support vimeo preview
export default defineNuxtComponent({
  setup () {
    let gtag
    if (typeof useGtag === 'function') {
      gtag = useGtag().gtag
    }

    return {
      gtag,
    }
  },

  props: {
    type: {
      type: String,
      default: 'youtube',
    },

    url: {
      type: String,
      default: '',
    },

    justPreview: {
      type: Boolean,
      default: false,
    },

    /**
     * Does not work for cloudinary videos
     */
    autoplay: {
      type: Boolean,
      default: false,
    },

    aspectRatio: {
      type: [String, Boolean],
      default: '16/9',
    },

    justPreviewPoster: {
      type: String,
      default: null,
    },

    thumbnailFile: {
      type: String,
      default: '0.jpg',
    },

    customThumbnail: {
      type: Object,
      default: null,
    },

    thumbnailClass: {
      type: String,
      default: null,
    },

    thumbnailBlur: {
      type: Boolean,
      default: false,
    },

    playButtonClass: {
      type: String,
      default: '',
    },

    alt: {
      type: String,
      default: '',
    },

    isHotelGallery: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['play'],

  data () {
    return {
      videoId: null,
      doPlay: false,
      initialized: false,
    }
  },

  computed: {
    play () {
      return this.doPlay || this.autoplay
    },
    isDemioUrl () {
      return this.url.startsWith('https://my.demio.com/')
    },
    location () {
      return this.videoId || this.url
    },

    src () {
      if (this.type === 'youtube') {
        return `https://www.youtube.com/embed/${this.location}?rel=0&autohide=1&mute=0&controls=1&autoplay=${this.play ? 1 : 0}`
      } else if (this.type === 'vimeo') {
        return `https://player.vimeo.com/video/${this.location}?autoplay=${this.play ? 1 : 0}&loop=1&autopause=0&api=1&controls=1&muted=0&playsinline=0`
      } else if (this.type === 'cloudinary') {
        return formatCloudinaryVideoPath(this.url)
      } else {
        return this.location
      }
    },

    thumbnailClasses () {
      const thumbnailClasses = []

      if (this.thumbnailClass) {
        thumbnailClasses.push(this.thumbnailClass)
      }

      if (this.thumbnailBlur) {
        thumbnailClasses.push('blur-sm')
      }

      return thumbnailClasses.join(' ')
    },
  },

  watch: {
    url (v) {
      if (v.includes('youtube.com')) {
        this.videoId = this.youtubeParser()
      }
    },
  },

  mounted () {
    if (this.url.includes('youtube.com')) {
      this.videoId = this.youtubeParser()
    }

    this.initialized = true
  },

  methods: {
    youtubeParser () {
      const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
      const match = this.url.match(regExp)
      return (match && match[7].length === 11) ? match[7] : false
    },

    onPlay () {
      if (!this.justPreview) {
        this.doPlay = true
      }
      this.$emit('play')

      if (typeof this.gtag === 'function') {
        this.gtag('event', 'view_video', {
          url: this.url,
          path: this.$router.currentRoute.value.fullPath,
        })
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.media-player {
  &-play-button {
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    transform: scale(1);

    img {
      filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.4));
    }
  }

  &-thumb {
    z-index: 2;
  }
}
.height-inherit {
  height: inherit;
}
</style>
